import { Injectable, computed, inject, signal } from '@angular/core';
import { MOCK_CASE, MockCase } from '../data/mock-case';
import { MOCK_PRODUCTS, MockProduct } from '../data/mock-products';
import { CaseContextService } from '../../../core/services/case-context.service';
import { Case } from '../../../core/services/case.model';

/**
 * Frame ids in the /calculator-v3 preview. Mirrors the 9 frames of the
 * comprehensive mockup at docs/audits/calculator-redesign-2026-05-05/
 * calculator-comprehensive-mockup.html.
 */
export type FrameId =
  | 'f1-empty'
  | 'f2-case'
  | 'f3-calculated'
  | 'f4-comparison'
  | 'f5-illustrate'
  | 'f6-compare'
  | 'f7-deliver'
  | 'f8-profile'
  | 'f9-save';

export type SolverMode = 'income' | 'premium' | 'start';
export type ProductType = 'FA' | 'IA' | 'RILA' | 'VA';

/** Six visible inputs on the form (advanced is collapsed). */
export interface Scenario {
  age: string;
  gender: 'M' | 'F';
  premium: string;
  startAge: string;
  productType: ProductType;
  state: string;
}

const STORAGE_KEY = 'calc-v3-state';

interface PersistedState {
  frame: FrameId;
  solver: SolverMode;
  scenario: Scenario;
  caseLoaded: boolean;
  /** State override applied after the case loads (mockup: IL → CA). */
  stateOverridden: boolean;
  /** Product picked for F8 / F5 / F7 routes. */
  selectedProductId: string | null;
  /** Product ids checked in the F4 grid (max 4 for V2 Compare handoff). */
  selectedForCompare: string[];
}

const DEFAULT_STATE: PersistedState = {
  frame: 'f1-empty',
  solver: 'income',
  scenario: { age: '', gender: 'M', premium: '', startAge: '', productType: 'IA', state: '' },
  caseLoaded: false,
  stateOverridden: false,
  selectedProductId: null,
  // Empty by default — F6 picks() falls back to MOCK_PRODUCTS.slice(0, 3)
  // when nothing is selected. Pre-populating with slug ids would shadow
  // any real numeric ids the user toggles in F4 (slugs lack riderRowId,
  // forcing F6 onto the fallback engine path instead of GetIllustration).
  selectedForCompare: [],
};

@Injectable({ providedIn: 'root' })
export class CalculatorV3StateService {
  private readonly caseCtx = inject(CaseContextService);
  private readonly _frame = signal<FrameId>(DEFAULT_STATE.frame);
  private readonly _solver = signal<SolverMode>(DEFAULT_STATE.solver);
  private readonly _scenario = signal<Scenario>(DEFAULT_STATE.scenario);
  private readonly _caseLoaded = signal<boolean>(DEFAULT_STATE.caseLoaded);
  private readonly _stateOverridden = signal<boolean>(DEFAULT_STATE.stateOverridden);
  private readonly _selectedProductId = signal<string | null>(DEFAULT_STATE.selectedProductId);
  private readonly _selectedForCompare = signal<string[]>(DEFAULT_STATE.selectedForCompare);
  /** Resolved card for the current selection. Populated when F3/F4 hand off
   *  with a real RealRankedCard, so F5/F7/F8 can read display fields without
   *  re-looking-up by id (which fails when real numeric ids don't match the
   *  slug-keyed MOCK_PRODUCTS). Not persisted — re-derived on next selection. */
  private readonly _selectedCard = signal<MockProduct | null>(null);
  /** Cache of the most recent ranked cards from F3/F4. Used by F6's picks()
   *  to resolve numeric ids back to displayable cards. Not persisted. */
  private readonly _lastCards = signal<MockProduct[]>([]);

  readonly frame = this._frame.asReadonly();
  readonly solver = this._solver.asReadonly();
  readonly scenario = this._scenario.asReadonly();
  readonly caseLoaded = this._caseLoaded.asReadonly();
  readonly stateOverridden = this._stateOverridden.asReadonly();
  readonly selectedProductId = this._selectedProductId.asReadonly();
  readonly selectedForCompare = this._selectedForCompare.asReadonly();
  readonly selectedCard = this._selectedCard.asReadonly();
  readonly lastCards = this._lastCards.asReadonly();

  /** Reactive case data. When the broker has an active case bound (set via
   *  /broker/cases or the case bar), reads come from the live Case row;
   *  otherwise falls back to MOCK_CASE so the dev preview still works
   *  without a session. Templates read `caseData()` for change detection. */
  readonly caseData = computed<MockCase>(() => {
    const active = this.caseCtx.activeCase();
    return active ? caseToMockShape(active) : MOCK_CASE;
  });

  /** Effective state field, accounting for the override. Pre-override = case
   *  state ('IL'); post-override = 'CA'. When no case is loaded, returns the
   *  raw scenario.state. */
  readonly effectiveState = computed<string>(() => {
    if (!this._caseLoaded()) return this._scenario().state;
    return this._stateOverridden() ? 'CA' : this.caseData().state;
  });

  /** True when there are enough inputs to enable Calculate (F1). Mirrors
   *  the mockup's "≥ 2 fields filled" heuristic. */
  readonly canCalculate = computed<boolean>(() => {
    if (this._caseLoaded()) return true;
    const s = this._scenario();
    const filled = [s.age, s.premium, s.startAge, s.state].filter(v => v && v.trim().length > 0).length;
    return filled >= 2;
  });

  constructor() {
    this.hydrate();
  }

  // ─── Frame navigation ─────────────────────────────────────────────────
  goTo(frame: FrameId): void {
    this._frame.set(frame);
    this.persist();
    // mockup behavior: scroll to top on every frame change
    queueMicrotask(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  // ─── Solver / scenario edits ──────────────────────────────────────────
  setSolver(mode: SolverMode): void {
    this._solver.set(mode);
    this.persist();
  }

  patchScenario(patch: Partial<Scenario>): void {
    this._scenario.update(s => ({ ...s, ...patch }));
    this.persist();
  }

  // ─── Case loading ─────────────────────────────────────────────────────
  /** F1 → F2: prefill scenario from MOCK_CASE, then jump to F2.
   *  Matches mockup behavior: state stays as the case's IL initially, then
   *  the user can override to CA in F2. */
  loadCase(): void {
    const c = this.caseData();
    this._scenario.set({
      age: String(c.age),
      gender: c.gender,
      premium: String(c.premium),
      startAge: '65',
      productType: c.productType,
      state: c.state,
    });
    this._caseLoaded.set(true);
    this._stateOverridden.set(false);
    this.goTo('f2-case');
  }

  /** F2: user typed CA over the case's IL. */
  applyStateOverride(): void {
    this._stateOverridden.set(true);
    this._scenario.update(s => ({ ...s, state: 'CA' }));
    this.persist();
  }

  /** F2: revert link clicked on the override field. */
  revertStateOverride(): void {
    this._stateOverridden.set(false);
    this._scenario.update(s => ({ ...s, state: this.caseData().state }));
    this.persist();
  }

  // ─── Selection (F4 grid → F6 V2 Compare) ──────────────────────────────
  toggleProductInCompare(productId: string): void {
    this._selectedForCompare.update(ids =>
      ids.includes(productId) ? ids.filter(id => id !== productId) : [...ids, productId]
    );
    this.persist();
  }

  selectAllForCompare(productIds: string[]): void {
    this._selectedForCompare.set(productIds);
    this.persist();
  }

  clearCompareSelection(): void {
    this._selectedForCompare.set([]);
    this.persist();
  }

  setSelectedProduct(id: string | null, card?: MockProduct | null): void {
    this._selectedProductId.set(id);
    if (card !== undefined) this._selectedCard.set(card);
    this.persist();
  }

  /** F3/F4 push their latest ranked output here so F6 can resolve numeric
   *  ids back to displayable cards. Runtime cache only — not persisted. */
  setLastCards(cards: MockProduct[]): void {
    this._lastCards.set(cards);
  }

  /** Resolve a card by id — tries the live ranked-cards cache first
   *  (real numeric ids), falls back to the static mock dataset (slug ids). */
  resolveCardById(id: string): MockProduct | undefined {
    return this._lastCards().find(c => c.id === id)
        ?? MOCK_PRODUCTS.find(c => c.id === id);
  }

  // ─── Reset ────────────────────────────────────────────────────────────
  reset(): void {
    this._frame.set(DEFAULT_STATE.frame);
    this._solver.set(DEFAULT_STATE.solver);
    this._scenario.set(DEFAULT_STATE.scenario);
    this._caseLoaded.set(DEFAULT_STATE.caseLoaded);
    this._stateOverridden.set(DEFAULT_STATE.stateOverridden);
    this._selectedProductId.set(DEFAULT_STATE.selectedProductId);
    this._selectedForCompare.set([...DEFAULT_STATE.selectedForCompare]);
    this._selectedCard.set(null);
    this._lastCards.set([]);
    this.persist();
  }

  // ─── Persistence (localStorage) ───────────────────────────────────────
  private persist(): void {
    try {
      const payload: PersistedState = {
        frame: this._frame(),
        solver: this._solver(),
        scenario: this._scenario(),
        caseLoaded: this._caseLoaded(),
        stateOverridden: this._stateOverridden(),
        selectedProductId: this._selectedProductId(),
        selectedForCompare: this._selectedForCompare(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore quota / disabled storage */
    }
  }

  private hydrate(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<PersistedState>;
      if (parsed.frame) this._frame.set(parsed.frame);
      if (parsed.solver) this._solver.set(parsed.solver);
      if (parsed.scenario) this._scenario.set({ ...DEFAULT_STATE.scenario, ...parsed.scenario });
      if (typeof parsed.caseLoaded === 'boolean') this._caseLoaded.set(parsed.caseLoaded);
      if (typeof parsed.stateOverridden === 'boolean') this._stateOverridden.set(parsed.stateOverridden);
      if (parsed.selectedProductId !== undefined) this._selectedProductId.set(parsed.selectedProductId);
      if (Array.isArray(parsed.selectedForCompare)) this._selectedForCompare.set(parsed.selectedForCompare);
    } catch {
      /* corrupted storage — fall through to defaults */
    }
  }
}

/** Map a real Case row into the legacy MockCase shape v3's templates expect.
 *  Lossy by design — MockCase has fewer fields than Case. Templates read
 *  caseCode, clientName, age, premium, funding, state, productType, ownership,
 *  paymentFrequency, gender; everything else on Case is dropped here.
 *
 *  Defaults reflect what an exploration case looks like (no client) so
 *  templates don't render "null" or undefined when the case is unbound. */
function caseToMockShape(c: Case): MockCase {
  return {
    caseId: c.caseCode,
    caseCode: c.caseCode,
    clientName: c.name || 'Exploration',
    age: c.age ?? 60,
    premium: c.premium ?? 250_000,
    funding: c.taxPref === 'q' ? 'qualified' : c.taxPref === 'nq' ? 'nq' : 'qualified',
    state: c.state ?? 'IL',
    // Case row doesn't carry productType — default to IA (v3's only supported
    // type today; the engine + Benefit API only return IA riders).
    productType: 'IA',
    ownership: c.spouseAge != null ? 'joint' : 'single',
    paymentFrequency: c.paymentFrequency ?? 'annual',
    gender: c.gender ?? 'M',
  };
}
